import {
  Button,
  makeStyles,
  Paper,
  PaperProps,
  TextFieldProps,
  Typography,
} from '@material-ui/core'
import React, { useState } from 'react'
import { PasswordInfo, useChangePassword } from '../api'
import { strings } from '../strings'
import { SubmitHandler, useForm } from 'react-hook-form'
import { LoadingButton } from '../LoadingButton'
import { useToken } from '../App'
import { useCaptcha } from '../Captcha'
import { PasswordField } from '../PasswordField'

interface ChangePasswordProps extends PaperProps {
  username: string
  onCancel: () => void
}

export const ChangePassword = React.forwardRef<unknown, ChangePasswordProps>((props, ref) => {
  const classes = useStyles()
  const { userRole } = useToken()
  const changePassword = useChangePassword()
  const [captcha, refreshCaptcha, getCaptchaInfo] = useCaptcha({ fullWidth: true })
  const {
    register,
    handleSubmit,
    formState: { isDirty },
    errors,
  } = useForm<PasswordInfo>()
  const [{ inProgress, error }, setState] = useState({
    inProgress: false,
    error: '',
  })

  const { username, className, onCancel: handleCancel } = props

  const onSubmit: SubmitHandler<PasswordInfo> = (formData) => {
    setState({ inProgress: true, error: '' })
    const captchaInfo = getCaptchaInfo()

    changePassword(formData, captchaInfo)
      .then(() => {
        setState({ inProgress: false, error: '' })
        handleCancel()
      })
      .catch((err) => {
        setState({ inProgress: false, error: err.message })
        refreshCaptcha()
      })
  }

  const hasPermission = userRole === 'admin' && username !== 'admin'

  return (
    <Paper className={className}>
      <form noValidate className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <input type="hidden" name="username" defaultValue={username} ref={register} />
        {!hasPermission && (
          <CustomPasswordField
            name="oldPassword"
            label={strings.currentPassword}
            inputRef={register({ required: !hasPermission })}
            error={!!errors.oldPassword}
          />
        )}
        <CustomPasswordField
          name="newPassword"
          label={strings.newPassword}
          inputRef={register({ required: true })}
          error={!!errors.newPassword}
        />
        {captcha}
        <Typography color="error">{error}</Typography>
        <div className={classes.buttons}>
          <div>
            <LoadingButton
              type="submit"
              variant="outlined"
              color="primary"
              text={strings.apply}
              loading={inProgress}
              disabled={!isDirty || inProgress}
            />
          </div>
          <div>
            <Button type="button" variant="outlined" color="secondary" onClick={handleCancel}>
              {strings.cancel}
            </Button>
          </div>
        </div>
      </form>
    </Paper>
  )
})

function CustomPasswordField(props: TextFieldProps) {
  return <PasswordField autoComplete="off" variant="outlined" fullWidth {...props} />
}

const useStyles = makeStyles((theme) => ({
  form: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    '& .MuiTextField-root': {
      margin: theme.spacing(1, 0),
    },
  },
  buttons: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: theme.spacing(3),
    marginTop: theme.spacing(1),
    '& > div:last-child': {
      textAlign: 'right',
    },
  },
}))
